<template>
  <v-sheet class="py-6 px-8">
    <p class="text-h6 font-weight-bold grey--text text--darken-3 text-center mb-6">프로모션 추가하기</p>

    <v-sheet class="mt-2 px-4">
      <v-text-field class="rounded-10" label="이름" dense outlined v-model.trim="create.name"></v-text-field>
      <v-text-field class="rounded-10" label="코드" dense outlined v-model.trim="create.code"></v-text-field>
      <!-- 상품 설정 -->
      <div class="font-weight-bold text-body-1 mb-4">상품 설정</div>
      <div v-if="create.products.length > 0" class="mb-4">
        <ProductCard v-for="(product, index) in create.products" :key="product.id" :product="product" @edit="editProduct(index)" @delete="deleteProduct(index)" />
      </div>
      <v-alert v-else type="info" text class="mb-4"> 등록된 상품이 없습니다. 아래 버튼을 클릭하여 상품을 추가해주세요. </v-alert>
      <v-btn v-if="availableProductIds.length > 0" color="primary" outlined @click="showAddProductDialog" class="mb-8">
        <v-icon left>mdi-plus</v-icon>
        상품 추가
      </v-btn>

      <!-- 옵션 상품 설정 -->
      <div class="font-weight-bold text-body-1 mb-4">옵션 상품 설정</div>
      <div v-if="create.optionProducts && create.optionProducts.length > 0" class="mb-4">
        <v-chip v-for="product in create.optionProducts" :key="product.id" class="ma-1" close @click:close="removeOptionProduct(product.id)">
          {{ getOptionProductTitle(product) }} ({{ product.price.toLocaleString() }}원)
        </v-chip>
      </div>
      <v-alert v-else type="info" text class="mb-4"> 선택된 옵션 상품이 없습니다. 아래 버튼을 클릭하여 옵션 상품을 추가해주세요. </v-alert>
      <v-btn color="success" outlined @click="showOptionProductDialog" class="mb-8">
        <v-icon left>mdi-plus</v-icon>
        옵션 상품 추가
      </v-btn>

      <div class="font-weight-bold text-body-1 py-2">컨셉 선택</div>
      <v-select outlined dense :items="conceptList" item-text="title" item-value="id" v-model="create.conceptId" label="컨셉" :key="'concept' + conceptComponentKey"></v-select>

      <div class="font-weight-bold text-body-1 py-2">프로모션 유효기간</div>
      <v-row>
        <v-menu v-model="openAtMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
          <template v-slot:activator="{ on, attrs }">
            <v-text-field v-model="create.openAtDate" label="링크시작일" prepend-icon="mdi-calendar" readonly v-bind="attrs" v-on="on" color="green"></v-text-field>
          </template>
          <v-date-picker v-model="create.openAtDate" no-title scrollable color="green" @input="openAtMenu = false"></v-date-picker>
        </v-menu>
        <v-menu v-model="closeAtMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
          <template v-slot:activator="{ on, attrs }">
            <v-text-field v-model="create.closeAtDate" label="링크종료일" prepend-icon="mdi-calendar" readonly v-bind="attrs" v-on="on" color="warning"></v-text-field>
          </template>
          <v-date-picker v-model="create.closeAtDate" no-title scrollable color="warning" @input="closeAtMenu = false"> </v-date-picker>
        </v-menu>
      </v-row>

      <div class="font-weight-bold text-body-1 py-2">예약 가능 기간</div>
      <v-row>
        <v-menu v-model="startAtMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
          <template v-slot:activator="{ on, attrs }">
            <v-text-field v-model="create.startAtDate" label="예약 시작일자" prepend-icon="mdi-calendar" readonly v-bind="attrs" v-on="on" color="green"></v-text-field>
          </template>
          <v-date-picker v-model="create.startAtDate" no-title scrollable color="green" @input="startAtMenu = false"></v-date-picker>
        </v-menu>
        <v-menu v-model="endAtMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
          <template v-slot:activator="{ on, attrs }">
            <v-text-field v-model="create.endAtDate" label="예약 종료일자" prepend-icon="mdi-calendar" readonly v-bind="attrs" v-on="on" color="warning"></v-text-field>
          </template>
          <v-date-picker v-model="create.endAtDate" no-title scrollable color="warning" @input="endAtMenu = false"> </v-date-picker>
        </v-menu>
      </v-row>

      <div class="font-weight-bold text-h6 ml-4 mt-4">한글</div>
      <v-sheet class="mt-2">
        <div class="d-flex justify-space-between">
          <span class="grey--text text-body-2">필요한 상품에 대한 설명을 입력해 주세요</span>
          <v-btn color="primary" depressed outlined small @click="clear()">삭제</v-btn>
        </div>
        <!-- 설명 -->
        <TipTapWriter class="tiptap_style mt-2" :options="options" :key="'desc1' + componentKey" />
      </v-sheet>

      <div class="font-weight-bold text-h6 ml-4 mt-4">영어</div>
      <v-sheet class="mt-2">
        <div class="d-flex justify-space-between">
          <span class="grey--text text-body-2">필요한 상품에 대한 설명을 입력해 주세요</span>
          <v-btn color="primary" depressed outlined small @click="clear('en')">삭제</v-btn>
        </div>
        <!-- 설명 -->
        <TipTapWriter class="tiptap_style mt-2" :options="en.options" :key="'en-desc1' + en.componentKey" />
      </v-sheet>

      <div class="font-weight-bold text-h6 ml-4 mt-4">일본어</div>
      <v-sheet class="mt-2">
        <div class="d-flex justify-space-between">
          <span class="grey--text text-body-2">필요한 상품에 대한 설명을 입력해 주세요</span>
          <v-btn color="primary" depressed outlined small @click="clear('jp')">삭제</v-btn>
        </div>
        <!-- 설명 -->
        <TipTapWriter class="tiptap_style mt-2" :options="jp.options" :key="'jp-desc1' + jp.componentKey" />
      </v-sheet>

      <div class="d-inline-block">
        <v-checkbox v-model="create.isIndividual" class="shrink mt-4 ml-0" label="개별 운영 (프리미엄)" color="primary" hide-details dense></v-checkbox>
      </div>
      <!-- 개별 운영시간 설정 -->
      <v-sheet v-if="create.isIndividual" class="mt-4 pa-4" outlined>
        <div class="font-weight-bold text-h6 mb-4">개별 운영시간 설정</div>
        <div v-for="(item, index) in dayItems" :key="item" class="mb-2">
          <div class="font-weight-bold mb-4">{{ item }}</div>
          <WeekTimeComponent :key="'week-time' + index + componentKey" @change="(v) => setOperationTime(index, v)" :src="operationSettings[index]" />
        </div>
      </v-sheet>
    </v-sheet>
    <v-sheet class="d-flex justify-center mt-5">
      <v-btn class="rounded-10 px-6 mr-4" color="grey" large dark depressed @click="$emit('close')">
        <v-icon small left>mdi-cancel</v-icon>
        취소하기
      </v-btn>
      <v-btn class="rounded-10 px-6" color="primary" large dark depressed @click="createSubmit()">
        <v-icon small left>mdi-database-plus-outline</v-icon>
        추가하기
      </v-btn>
    </v-sheet>
    <!-- Product Edit Dialog -->
    <ProductEditDialog v-if="editingProduct" v-model="productEditDialog" :product="editingProduct" @save="onProductSave" />

    <!-- Option Product Select Dialog -->
    <v-dialog v-model="optionProductDialog" width="600" scrollable>
      <OptionProductSelectDialog v-model="optionProductDialog" :selected-products="create.optionProducts" @toggle="toggleOptionProduct" />
    </v-dialog>
  </v-sheet>
</template>
<script>
import TipTapWriter from "@/components/tiptap/Writer"
import WeekTimeComponent from "@/views/admin/operation_time/WeekTimeComponent"
import ProductEditDialog from "./ProductEditDialog.vue"
import ProductCard from "./ProductCard.vue"
import OptionProductSelectDialog from "./OptionProductSelectDialog.vue"
import { DEFAULT_PACKAGE_CODE } from "@/constant"
import { mapState } from "vuex"

export default {
  inject: ["concepts"],
  components: {
    TipTapWriter,
    WeekTimeComponent,
    ProductEditDialog,
    ProductCard,
    OptionProductSelectDialog,
  },
  data: () => ({
    yesOrNo: ["Y", "N"],
    create: {
      name: "",
      code: "",
      option1Price: 0,
      option2Price: 0,
      option3Price: 0,
      option4Price: 0,
      option5Price: 0,
      option6Price: 0,
      isEnable: true,
      startAtDate: null,
      endAtDate: null,
      openAtDate: null,
      closeAtDate: null,
      isTraveler: "N",
      conceptId: null,
      isIndividual: false,
      products: [],
      optionProducts: [],
    },
    startAtMenu: false,
    endAtMenu: false,
    openAtMenu: false,
    closeAtMenu: false,

    conceptComponentKey: 0,

    //TipTap
    options: {
      content: "",
      editable: true,
      supportImage: true,
      supportVideo: true,
    },
    componentKey: 0,

    en: {
      //TipTap
      options: {
        content: "",
        editable: true,
        supportImage: true,
        supportVideo: true,
      },
      componentKey: 0,
    },
    jp: {
      //TipTap
      options: {
        content: "",
        editable: true,
        supportImage: true,
        supportVideo: true,
      },
      componentKey: 0,
    },
    visibleCouponSelect: false,

    // 개별 운영시간 설정
    dayItems: ["월", "화", "수", "목", "금", "토", "일"],
    operationSettings: [[], [], [], [], [], [], []],

    // 그룹샷 초대 인원 수 옵션
    groupMemberItems: [
      { text: "2명", value: 2 },
      { text: "3명", value: 3 },
      { text: "4명", value: 4 },
      { text: "5명", value: 5 },
      { text: "6명", value: 6 },
    ],

    // Product edit dialog
    productEditDialog: false,
    editingProductIndex: -1,
    editingProduct: null,

    // Available product IDs
    allProductIds: ["option1", "option2", "option3", "option4", "option5", "option6"],

    // Option Product dialog
    optionProductDialog: false,
  }),

  computed: {
    availableProductIds() {
      const usedIds = this.create.products.map((p) => p.id)
      return this.allProductIds.filter((id) => !usedIds.includes(id))
    },
    conceptList() {
      const filtered = this.concepts.filter((c) => {
        if (this.create.isIndividual) {
          return c.productType === "premium"
        }
        return !c.productType || c.productType === "makershot"
      })
      return [{ title: "선택안함", id: null }, ...filtered]
    },
  },

  watch: {
    "create.isIndividual"() {
      const selectedConcept = this.concepts.find((c) => c.id === this.create.conceptId)
      if (selectedConcept) {
        const conceptType = selectedConcept.productType || "makershot"
        const expectedType = this.create.isIndividual ? "premium" : "makershot"
        if (conceptType !== expectedType) {
          this.create.conceptId = null
          this.conceptComponentKey++
        }
      }
    },
  },

  mounted() {
    this.$http.get("/api/promotion/select").then((res) => {
      const _defaultPrice = res.data.find((e) => e.code == "default")
      const defaultPrice = _defaultPrice
      if (defaultPrice) {
        this.create.option1Price = defaultPrice.option1Price
        this.create.option2Price = defaultPrice.option2Price
        this.create.option3Price = defaultPrice.option3Price
        this.create.option4Price = defaultPrice.option4Price
        this.create.option5Price = defaultPrice.option5Price
        this.create.option6Price = defaultPrice.option6Price
      }
    })
  },

  methods: {
    showOptionProductDialog() {
      this.optionProductDialog = true
    },

    toggleOptionProduct(product) {
      const index = this.create.optionProducts.findIndex((p) => p.id === product.id)
      if (index > -1) {
        this.create.optionProducts.splice(index, 1)
      } else {
        this.create.optionProducts.push(product)
      }
    },

    removeOptionProduct(productId) {
      const index = this.create.optionProducts.findIndex((p) => p.id === productId)
      if (index > -1) {
        this.create.optionProducts.splice(index, 1)
      }
    },

    getOptionProductTitle(product) {
      if (typeof product.title === "string") {
        try {
          const parsed = JSON.parse(product.title)
          return parsed.kr || product.title
        } catch {
          return product.title
        }
      } else if (product.title && typeof product.title === "object") {
        return product.title.kr || ""
      }
      return ""
    },

    showAddProductDialog() {
      const nextId = this.availableProductIds[0]
      const defaultPrices = {
        option1: this.create.option1Price,
        option2: this.create.option2Price,
        option3: this.create.option3Price,
        option4: this.create.option4Price,
        option5: this.create.option5Price,
        option6: this.create.option6Price,
      }

      this.editingProductIndex = -1 // -1 means adding new product
      this.editingProduct = {
        id: nextId,
        price: defaultPrices[nextId] || 0,
        maxpersons: nextId === "option1" || nextId === "option2" ? 1 : 2,
        type: nextId === "option1" || nextId === "option2" ? "single" : "group",
        freePicEditCount: 1,
        title: { kr: "", en: "", jp: "" },
        subtitle: { kr: "", en: "", jp: "" },
        order: this.create.products.length + 1,
      }
      this.productEditDialog = true
    },
    editProduct(index) {
      this.editingProductIndex = index
      this.editingProduct = JSON.parse(JSON.stringify(this.create.products[index]))
      this.productEditDialog = true
    },
    deleteProduct(index) {
      if (confirm("이 상품을 삭제하시겠습니까?")) {
        this.create.products.splice(index, 1)
        // Reorder remaining products
        this.create.products.forEach((product, i) => {
          product.order = i + 1
        })
      }
    },
    onProductSave(updatedProduct) {
      if (this.editingProductIndex === -1) {
        // Adding new product
        this.create.products.push(updatedProduct)
        // Sort by product ID order
        this.create.products.sort((a, b) => {
          const orderA = this.allProductIds.indexOf(a.id)
          const orderB = this.allProductIds.indexOf(b.id)
          return orderA - orderB
        })
      } else {
        // Editing existing product
        this.create.products[this.editingProductIndex] = updatedProduct
      }
    },
    clear(lang = "ko") {
      if (lang === "ko") {
        this.options.content = ""
        this.componentKey++
      } else if (lang === "en") {
        this.en.options.content = ""
        this.en.componentKey++
      } else if (lang === "jp") {
        this.jp.options.content = ""
        this.jp.componentKey++
      }
    },

    setOperationTime(index, value) {
      this.operationSettings[index] = value
    },

    // 목록
    createSubmit: _.debounce(async function () {
      // 제목 검증
      // if (this.create.name == "") {
      //   alert("이름을 입력해주세요")
      //   return
      // }

      // 컨셉소개 내용 검증
      if (this.create.code == "") {
        alert("프로모션 코드를 입력해주세요")
        return
      }

      // 상품 검증
      if (this.create.products.length === 0) {
        alert("최소 1개 이상의 상품을 추가해주세요")
        return
      }

      // 가격 검증
      const hasInvalidPrice = this.create.products.some((product) => product.price < 0)
      if (hasInvalidPrice) {
        alert("유효한 가격 정보를 입력해주세요")
        return
      }

      if (!this.create.endAtDate) {
        alert("종료일자를 입력해주세요")
        return
      }
      console.log(this.create.startAtDate)
      console.log(this.create.endAtDate)

      this.$http
        .post("/api/promotion/insert", {
          params: {
            code: this.create.code,
            name: this.create.name,
            option1Price: Number(this.create.products.find((p) => p.id === "option1")?.price || 0),
            option2Price: Number(this.create.products.find((p) => p.id === "option2")?.price || 0),
            option3Price: Number(this.create.products.find((p) => p.id === "option3")?.price || 0),
            option4Price: Number(this.create.products.find((p) => p.id === "option4")?.price || 0),
            option5Price: Number(this.create.products.find((p) => p.id === "option5")?.price || 0),
            option6Price: Number(this.create.products.find((p) => p.id === "option6")?.price || 0),
            products: JSON.stringify(this.create.products),
            conceptId: this.create.conceptId,
            enabled: this.create.isEnable,
            openAt: this.create.openAtDate ? this.dayjs(this.create.openAtDate).format("YYYY-MM-DD HH:mm:ss") : null,
            closeAt: this.create.closeAtDate ? this.dayjs(this.create.closeAtDate).format("YYYY-MM-DD HH:mm:ss") : null,
            startAt: this.create.startAtDate ? this.dayjs(this.create.startAtDate).format("YYYY-MM-DD HH:mm:ss") : null,
            endAt: this.create.endAtDate ? this.dayjs(this.create.endAtDate).format("YYYY-MM-DD HH:mm:ss") : null,
            description: this.options.content ? this.options.content : null,
            description_en: this.en.options.content ? this.en.options.content : null,
            description_jp: this.jp.options.content ? this.jp.options.content : null,
            isIndividual: this.create.isIndividual,
            optionProducts: this.create.optionProducts.length > 0 ? this.create.optionProducts.map((p) => p.id).join(",") : null,
          },
        })
        .then(async (res) => {
          if (res) {
            // 개별 운영시간 설정이 활성화된 경우 추가 저장
            if (this.create.isIndividual) {
              try {
                await this.$http.post("/api/individual_operation_time/insert", {
                  promotionCode: this.create.code,
                  mon: this.operationSettings[0].length > 0 ? this.operationSettings[0].join(",") : "",
                  tue: this.operationSettings[1].length > 0 ? this.operationSettings[1].join(",") : "",
                  wed: this.operationSettings[2].length > 0 ? this.operationSettings[2].join(",") : "",
                  thu: this.operationSettings[3].length > 0 ? this.operationSettings[3].join(",") : "",
                  fri: this.operationSettings[4].length > 0 ? this.operationSettings[4].join(",") : "",
                  sat: this.operationSettings[5].length > 0 ? this.operationSettings[5].join(",") : "",
                  sun: this.operationSettings[6].length > 0 ? this.operationSettings[6].join(",") : "",
                })
              } catch (e) {
                console.log("개별 운영시간 저장 실패:", e)
              }
            }

            alert("정상적으로 등록되었습니다.")
            this.$emit("close")
          } else {
            alert("등록되지 않았습니다")
          }
        })
        .catch((e) => {
          console.log(e)
          alert(this.$t("alert.error_page"))
        })
    }, 500),
  },
}
</script>
<style scoped>
.my-clipper {
  width: 100%;
}

.placeholder {
  width: 100%;
  height: 150px;
}
</style>
